import { useCallback, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  fetchMarketplacePage,
  MARKETPLACE_PAGE_SIZE,
} from "../lib/marketplaceSearch.js";

const marketplaceTypes = ["trips", "offers", "orders"];

export default function useMarketplaceData({
  userId,
  marketSearch,
  marketDate,
  orderSearch,
  orderStatus,
  initialTrips,
  onProfileLoaded,
  onProfileLoadError,
}) {
  const [trips, setTrips] = useState(initialTrips);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [tripsError, setTripsError] = useState("");
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersError, setOffersError] = useState("");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [tripsHasMore, setTripsHasMore] = useState(false);
  const [offersHasMore, setOffersHasMore] = useState(false);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const [tripsMoreLoading, setTripsMoreLoading] = useState(false);
  const [offersMoreLoading, setOffersMoreLoading] = useState(false);
  const [ordersMoreLoading, setOrdersMoreLoading] = useState(false);
  const [tripsMoreError, setTripsMoreError] = useState("");
  const [offersMoreError, setOffersMoreError] = useState("");
  const [ordersMoreError, setOrdersMoreError] = useState("");

  const options = useRef(null);
  options.current = {
    userId,
    marketSearch,
    marketDate,
    orderSearch,
    orderStatus,
    onProfileLoaded,
    onProfileLoadError,
  };
  const state = useRef(null);
  state.current = {
    trips,
    offers,
    orders,
    tripsHasMore,
    offersHasMore,
    ordersHasMore,
  };
  const loadRequest = useRef(0);
  const pageRequest = useRef({ trips: 0, offers: 0, orders: 0 });
  const pageLock = useRef({ trips: null, offers: null, orders: null });
  const hasLoaded = useRef(!supabase);

  const reload = useCallback(async () => {
    for (const type of marketplaceTypes) {
      pageRequest.current[type] += 1;
      pageLock.current[type] = null;
    }
    setTripsMoreLoading(false);
    setOffersMoreLoading(false);
    setOrdersMoreLoading(false);

    const current = options.current;
    if (!supabase || !current.userId) {
      loadRequest.current += 1;
      hasLoaded.current = !supabase;
      setTripsLoading(false);
      setOffersLoading(false);
      setOrdersLoading(false);
      setTripsHasMore(false);
      setOffersHasMore(false);
      setOrdersHasMore(false);
      return;
    }

    const requestId = ++loadRequest.current;
    const requestedUserId = current.userId;
    setTripsLoading(true);
    setTripsError("");
    setTripsMoreError("");
    setOffersLoading(true);
    setOffersError("");
    setOffersMoreError("");
    setOrdersLoading(true);
    setOrdersError("");
    setOrdersMoreError("");

    const results = await Promise.allSettled([
      supabase
        .from("profiles")
        .select("*")
        .eq("id", current.userId)
        .maybeSingle(),
      fetchMarketplacePage("trips", {
        query: current.marketSearch,
        date: current.marketDate,
      }),
      fetchMarketplacePage("offers", {
        query: current.marketSearch,
        date: current.marketDate,
      }),
      fetchMarketplacePage("orders", {
        query: current.orderSearch,
        status: current.orderStatus,
      }),
    ]);
    if (
      requestId !== loadRequest.current ||
      options.current.userId !== requestedUserId
    ) {
      return;
    }

    const [profileResult, tripsResult, offersResult, ordersResult] = results;
    const errorMessage = (result) =>
      result.status === "rejected"
        ? result.reason?.message || "Network request failed."
        : result.value.error?.message || "";
    const profileError = errorMessage(profileResult);
    const tripError = errorMessage(tripsResult);
    const offerError = errorMessage(offersResult);
    const orderError = errorMessage(ordersResult);

    if (profileError) {
      options.current.onProfileLoadError(
        `Could not load profile: ${profileError}`,
      );
    } else if (profileResult.value.data) {
      options.current.onProfileLoaded(profileResult.value.data);
    }

    if (tripError) {
      setTripsError(tripError);
    } else {
      const rows = tripsResult.value.data || [];
      setTrips(rows.slice(0, MARKETPLACE_PAGE_SIZE));
      setTripsHasMore(rows.length > MARKETPLACE_PAGE_SIZE);
    }
    if (offerError) {
      setOffers([]);
      setOffersHasMore(false);
      setOffersError(offerError);
    } else {
      const rows = offersResult.value.data || [];
      setOffers(rows.slice(0, MARKETPLACE_PAGE_SIZE));
      setOffersHasMore(rows.length > MARKETPLACE_PAGE_SIZE);
    }
    if (orderError) {
      setOrdersError(orderError);
    } else {
      const rows = ordersResult.value.data || [];
      setOrders(rows.slice(0, MARKETPLACE_PAGE_SIZE));
      setOrdersHasMore(rows.length > MARKETPLACE_PAGE_SIZE);
    }

    hasLoaded.current = true;
    setTripsLoading(false);
    setOffersLoading(false);
    setOrdersLoading(false);
  }, []);

  const loadMoreMarketplace = useCallback(async (type) => {
    const current = options.current;
    const currentState = state.current;
    const config = {
      trips: {
        rows: currentState.trips,
        hasMore: currentState.tripsHasMore,
        dateField: "travel_date",
        ascending: true,
        setRows: setTrips,
        setHasMore: setTripsHasMore,
        setLoading: setTripsMoreLoading,
        setError: setTripsMoreError,
        filters: { query: current.marketSearch, date: current.marketDate },
      },
      offers: {
        rows: currentState.offers,
        hasMore: currentState.offersHasMore,
        dateField: "planned_date",
        ascending: true,
        setRows: setOffers,
        setHasMore: setOffersHasMore,
        setLoading: setOffersMoreLoading,
        setError: setOffersMoreError,
        filters: { query: current.marketSearch, date: current.marketDate },
      },
      orders: {
        rows: currentState.orders,
        hasMore: currentState.ordersHasMore,
        dateField: "created_at",
        ascending: false,
        setRows: setOrders,
        setHasMore: setOrdersHasMore,
        setLoading: setOrdersMoreLoading,
        setError: setOrdersMoreError,
        filters: { query: current.orderSearch, status: current.orderStatus },
      },
    }[type];
    if (
      !supabase ||
      !current.userId ||
      !config?.hasMore ||
      pageLock.current[type] !== null
    ) {
      return;
    }

    const requestId = pageRequest.current[type];
    pageLock.current[type] = requestId;
    config.setLoading(true);
    config.setError("");
    const cursor = config.rows[config.rows.length - 1];
    try {
      const { data, error } = await fetchMarketplacePage(type, {
        ...config.filters,
        cursor,
      });
      if (pageRequest.current[type] !== requestId) return;
      if (error) throw error;

      const rows = data || [];
      const page = rows.slice(0, MARKETPLACE_PAGE_SIZE);
      config.setRows((currentRows) => {
        const merged = new Map(currentRows.map((item) => [item.id, item]));
        for (const item of page) merged.set(item.id, item);
        return [...merged.values()].sort((a, b) => {
          const dateOrder = String(a[config.dateField]).localeCompare(
            String(b[config.dateField]),
          );
          return (
            (config.ascending ? dateOrder : -dateOrder) ||
            (config.ascending ? 1 : -1) * a.id.localeCompare(b.id)
          );
        });
      });
      config.setHasMore(rows.length > MARKETPLACE_PAGE_SIZE);
    } catch (error) {
      if (pageRequest.current[type] === requestId) {
        config.setError(error.message || "Network request failed.");
      }
    } finally {
      if (pageLock.current[type] === requestId) {
        pageLock.current[type] = null;
        config.setLoading(false);
      }
    }
  }, []);

  return {
    trips,
    tripsLoading,
    setTripsLoading,
    tripsError,
    offers,
    offersLoading,
    setOffersLoading,
    offersError,
    orders,
    ordersLoading,
    setOrdersLoading,
    ordersError,
    tripsHasMore,
    offersHasMore,
    ordersHasMore,
    tripsMoreLoading,
    offersMoreLoading,
    ordersMoreLoading,
    tripsMoreError,
    offersMoreError,
    ordersMoreError,
    marketplaceHasLoaded: hasLoaded,
    reload,
    loadMoreMarketplace,
  };
}
